import { useState, useEffect } from 'react';
import { Download, X, Share } from 'lucide-react';
import { Button } from '../components/ui/button';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone === true;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  useEffect(() => {
    if (isStandalone || sessionStorage.getItem('pwa-dismissed')) return;

    // Android / Chrome: capture the install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari: show manual instructions
    if (isIOS && isSafari) {
      setShowIOSPrompt(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isStandalone, isIOS, isSafari]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('pwa-dismissed', '1');
  };

  if (isStandalone || dismissed) return null;
  if (!deferredPrompt && !showIOSPrompt) return null;

  return (
    <div data-testid="pwa-install-banner" className="fixed bottom-4 left-4 right-4 z-50 bg-[#1e3a5f] text-white rounded-xl shadow-2xl p-4 flex items-center gap-3 max-w-md mx-auto animate-in slide-in-from-bottom-4">
      <div className="bg-white/20 rounded-lg p-2 shrink-0">
        <Download className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">Install Ocean Fishing Bissau</p>
        {deferredPrompt ? (
          <p className="text-xs text-white/70">Add to home screen for quick access</p>
        ) : (
          <p className="text-xs text-white/70 leading-tight">
            Tap <Share className="w-3 h-3 inline -mt-0.5" /> then "Add to Home Screen"
          </p>
        )}
      </div>
      {deferredPrompt ? (
        <Button
          data-testid="pwa-install-btn"
          size="sm"
          onClick={handleInstall}
          className="bg-white text-[#1e3a5f] hover:bg-white/90 font-semibold shrink-0"
        >
          Install
        </Button>
      ) : null}
      <button
        data-testid="pwa-dismiss-btn"
        onClick={handleDismiss}
        className="text-white/60 hover:text-white shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default PWAInstallPrompt;
